/**
 * Smooth-scroll the nearest scrollable ancestor so `el` sits at its top; falls
 * back to `scrollIntoView` when nothing between it and the body scrolls.
 */
export const scrollWithinNearestContainer = (el: HTMLElement): void => {
  let cur: HTMLElement | null = el.parentElement;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    const overflowY = (
      window.getComputedStyle(cur).overflowY || ""
    ).toLowerCase();
    const scrollable =
      (overflowY === "auto" || overflowY === "scroll") &&
      cur.scrollHeight > cur.clientHeight + 1;
    if (scrollable) {
      const top =
        el.getBoundingClientRect().top -
        cur.getBoundingClientRect().top +
        cur.scrollTop;
      cur.scrollTo({ top, behavior: "smooth" });
      return;
    }
    cur = cur.parentElement;
  }
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/**
 * Scroll to `#id` once it exists, retrying briefly so it works right after a
 * section mounts. Returns a cancel function.
 */
export const scheduleScrollToDomId = (
  id: string,
  {
    attempts = 20,
    intervalMs = 100
  }: { attempts?: number; intervalMs?: number } = {}
): (() => void) => {
  let timer: number | null = null;
  let cancelled = false;
  const tick = (left: number) => {
    if (cancelled) return;
    const el = document.getElementById(id);
    if (el) {
      scrollWithinNearestContainer(el);
      return;
    }
    if (left <= 0) return;
    timer = window.setTimeout(() => tick(left - 1), intervalMs);
  };
  timer = window.setTimeout(() => tick(attempts), 0);
  return () => {
    cancelled = true;
    if (timer !== null) window.clearTimeout(timer);
  };
};
